import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DocsCodeBlock from '../Components/Docs/DocsCodeBlock';
import DocsCodePreview from '../Components/Docs/DocsCodePreview';
import DocsGuide from '../Components/Docs/DocsGuide';

const EXAMPLE_ORIGIN = 'https://try.magicfurnish.com';
const EMBED_BASE = `${EXAMPLE_ORIGIN}/external-ai-upload`;

const EMBED_IFRAME_TEXT = `<iframe
  id="magicfurnish-creator"
  title="Create a furnished floor plan"
  width="100%"
  height="240"
  style="max-width: 360px; background: transparent; border: none; overflow: hidden;">
</iframe>`;

const LISTEN_HANDLER_TEXT = `const iframe = document.getElementById('magicfurnish-creator');
const MAGICFURNISH_ORIGIN = '${EXAMPLE_ORIGIN}';
const launchedListingId = 'YOUR_LISTING_ID';

async function onMagicFurnishMessage(event) {
  if (event.origin !== MAGICFURNISH_ORIGIN) return;
  if (!iframe || event.source !== iframe.contentWindow) return;
  if (event.data?.type !== 'mf:complete') return;

  const { galleryUrl, ref: echoedRef } = event.data;
  if (typeof galleryUrl !== 'string' || !galleryUrl) return;

  let parsedGalleryUrl;
  try {
    parsedGalleryUrl = new URL(galleryUrl);
  } catch {
    return;
  }
  if (parsedGalleryUrl.origin !== MAGICFURNISH_ORIGIN) return;

  if (
    typeof echoedRef === 'string' &&
    echoedRef.length > 0 &&
    echoedRef !== launchedListingId
  ) {
    return;
  }

  try {
    await persistGalleryUrlForListing(launchedListingId, galleryUrl);
    dismissOrReplaceUploader();
    window.removeEventListener('message', onMagicFurnishMessage);
  } catch (error) {
    console.error('Failed to persist gallery URL', error);
  }
}

window.addEventListener('message', onMagicFurnishMessage);
iframe.src =
  '${EMBED_BASE}?host_app=acme&ref=' +
  encodeURIComponent(launchedListingId);`;

const MF_COMPLETE_PAYLOAD_TEXT = `{
  "type": "mf:complete",
  "galleryUrl": "${EXAMPLE_ORIGIN}/floor-gallery/507f1f77bcf86cd799439011",
  "uniqueId": "f47ac10b-58cc-4372-a567-0e02b2c3d479",
  "ref": "YOUR_LISTING_ID"
}`;

const DOC_NAV_GROUPS = [
  {
    heading: 'Start here',
    primary: true,
    items: [
      { id: 'quickstart', label: 'Quickstart' },
      { id: 'embed', label: 'Embed' },
      { id: 'listen', label: 'Persist' },
      { id: 'display', label: 'Store the link' },
      { id: 'checklist', label: 'Embed checklist' },
    ],
  },
  {
    heading: 'Contract reference',
    items: [
      { id: 'parameters', label: 'Parameters & payload' },
      { id: 'galleries', label: 'Re-uploads & failures' },
    ],
  },
  {
    heading: 'Security & operations',
    items: [
      { id: 'security', label: 'Security' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
    ],
  },
  {
    heading: 'Additional resources',
    items: [
      { id: 'show', label: 'Show the plan' },
      { id: 'smple', label: 'S.MPLE' },
    ],
  },
  {
    heading: 'Production launch',
    items: [{ id: 'kickoff', label: 'Launch inputs' }],
  },
];

const CreateGuideDocs = () => {
  const [listenerExpanded, setListenerExpanded] = useState(false);

  return (
    <DocsGuide
      documentTitle="MagicFurnish Create Guide"
      navGroups={DOC_NAV_GROUPS}
      lastUpdated="November 4, 2026"
    >
      <header className="docs-hero">
        <p className="docs-hero-eyebrow">Create Guide</p>
        <h1 className="doc-title">
          One floor plan in.
          <br />
          One link out.
        </h1>
        <p className="doc-sub">
          Embed the MagicFurnish uploader in your product. When a floor plan is ready, store the
          returned <code>galleryUrl</code> on that listing. Replace the sample host below with the
          MagicFurnish host for your account.
        </p>
      </header>

      <section className="quickstart" id="quickstart">
        <h2 className="quickstart-title">Quickstart</h2>
        <ol className="quickstart-steps">
          <li>
            <a href="#embed"><strong>Embed</strong></a>: create the iframe without{' '}
            <code>src</code>, register the <code>message</code> listener, then assign{' '}
            <code>{EMBED_BASE}?host_app=acme&amp;ref={'{listing_id}'}</code>.
          </li>
          <li>
            <a href="#listen"><strong>Persist</strong></a>: on <code>mf:complete</code>, store the
            returned <code>galleryUrl</code> on the listing that launched the iframe.
          </li>
          <li>
            <a href="#display"><strong>Keep the link</strong></a>: that stored URL is what a
            listing page shows later. See <Link to="/embed">the listing embed guide</Link>.
          </li>
        </ol>
      </section>

      <section className="phase" id="phase-1">
        <div className="step" id="embed">
          <div className="step-head">
            <span className="step-num">01</span>
            <span className="step-name">Embed the uploader</span>
          </div>
          <p>
            Create the iframe without a <code>src</code>. After the listener is registered, assign
            the uploader URL. The path is <code>/external-ai-upload</code> on your MagicFurnish
            host.
          </p>
          <p>
            <code>{EMBED_BASE}?host_app=acme&amp;ref={'{listing_id}'}</code>
          </p>
          <p>
            URL-encode the listing id. <code>host_app</code> is a short name for your product.
            Use your own name. S.MPLE&apos;s name belongs only on the S.MPLE guide. If the host
            page sets CSP, allow <code>{EXAMPLE_ORIGIN}</code> in <code>frame-src</code>.
          </p>
          <DocsCodeBlock text={EMBED_IFRAME_TEXT} label="iframe">
            &lt;<span className="k">iframe</span>
            {'\n  '}
            <span className="k">id</span>=<span className="s">&quot;magicfurnish-creator&quot;</span>
            {'\n  '}
            <span className="k">title</span>=<span className="s">&quot;Create a furnished floor plan&quot;</span>
            {'\n  '}
            <span className="k">width</span>=<span className="s">&quot;100%&quot;</span>
            {'\n  '}
            <span className="k">height</span>=<span className="s">&quot;240&quot;</span>
            {'\n  '}
            <span className="k">style</span>=<span className="s">&quot;max-width: 360px; background: transparent; border: none; overflow: hidden;&quot;</span>&gt;
            {'\n'}
            &lt;/<span className="k">iframe</span>&gt;
          </DocsCodeBlock>
          <p>
            Supported at 320–360px wide and 240px tall. Accepted upload: one JPG, JPEG, or PNG
            floor plan, up to 10&nbsp;MB.
          </p>
        </div>

        <div className="step" id="listen">
          <div className="step-head">
            <span className="step-num">02</span>
            <span className="step-name">Persist mf:complete</span>
          </div>
          <p>
            Attach the listener on the iframe&apos;s immediate parent <strong>before</strong>{' '}
            setting <code>iframe.src</code>. Validate origin, source, and payload.
          </p>
          <ul>
            <li>
              <code>mf:complete</code> is posted when processing finishes and a{' '}
              <code>galleryUrl</code> is ready. Uploading the file alone does not send it.
            </li>
            <li>
              Store <code>galleryUrl</code> against the listing id you launched with. A newer URL
              for that listing replaces the one you stored.
            </li>
            <li>
              If saving fails, keep the listener and the returned URL, and retry the save without
              another upload.
            </li>
          </ul>
          <div className="docs-note">
            Validate <code>event.origin</code> exactly. The iframe posts with{' '}
            <code>targetOrigin: &quot;*&quot;</code>.
          </div>
          <DocsCodePreview
            label="message listener"
            expanded={listenerExpanded}
            onToggle={() => setListenerExpanded((open) => !open)}
          >
            <DocsCodeBlock text={LISTEN_HANDLER_TEXT} label="message listener" />
          </DocsCodePreview>
          <div className="docs-note">
            <code>persistGalleryUrlForListing</code> and <code>dismissOrReplaceUploader</code> are
            your product&apos;s save and close steps.
          </div>
        </div>

        <div className="step" id="display">
          <div className="step-head">
            <span className="step-num">03</span>
            <span className="step-name">Store the link</span>
          </div>
          <p>
            The create step is done when <code>galleryUrl</code> is stored on the listing. Open
            that URL in the top-level window, or hand it to the site that shows the listing.
          </p>
          <p>
            Showing it inside a property site or listing page is the{' '}
            <Link to="/embed">listing embed guide</Link>.
          </p>
        </div>

        <div className="step" id="checklist">
          <h3 className="ref-heading">Embed checklist</h3>
          <ul className="checklist">
            <li>Listener registered before <code>iframe.src</code></li>
            <li>
              Exact <code>event.origin</code> matches your MagicFurnish host
            </li>
            <li>
              Exact <code>event.source === iframe.contentWindow</code>
            </li>
            <li>
              Valid <code>galleryUrl</code> on that same host
            </li>
            <li>
              <code>host_app</code> is your product name, and <code>ref</code> is the listing id
            </li>
            <li>Stored against the listing that launched the iframe</li>
            <li>Uploader dismissed only after the save succeeds</li>
          </ul>
        </div>
      </section>

      <div className="docs-layer-head" role="presentation">
        <span className="docs-layer-kicker">Contract reference</span>
      </div>

      <section className="phase docs-phase--ref" id="parameters">
        <h2 className="phase-title">Parameters &amp; payload</h2>
        <p>Set these on <code>iframe.src</code> after the listener is registered.</p>
        <table className="scope-ownership">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Requirement</th>
              <th>Behavior</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>ref</code></td>
              <td>Required</td>
              <td>
                Your listing id. URL-encode it. Echoed on <code>mf:complete</code>. This is the
                key you store the URL against, and the id used later to look the URL up.
              </td>
            </tr>
            <tr>
              <td><code>host_app</code></td>
              <td>Required</td>
              <td>
                A short name for your product, such as <code>acme</code>. Together with{' '}
                <code>ref</code>, this attributes the upload to that listing. S.MPLE uses its own
                name on the S.MPLE guide.
              </td>
            </tr>
            <tr>
              <td><code>return_url</code></td>
              <td>Optional</td>
              <td>
                Snake_case only (<code>return_url</code>, not <code>returnUrl</code>). Where the
                in-card <em>View furnished layout</em> control goes after the upload. Omit it to
                use the iframe&apos;s history instead.
              </td>
            </tr>
          </tbody>
        </table>

        <h3 className="ref-heading">Event payload</h3>
        <DocsCodeBlock text={MF_COMPLETE_PAYLOAD_TEXT} label="mf:complete payload">
          {'{'}
          {'\n  '}
          <span className="k">&quot;type&quot;</span>: <span className="s">&quot;mf:complete&quot;</span>,
          {'\n  '}
          <span className="k">&quot;galleryUrl&quot;</span>: <span className="s">&quot;{EXAMPLE_ORIGIN}/floor-gallery/507f1f77bcf86cd799439011&quot;</span>,
          {'\n  '}
          <span className="k">&quot;uniqueId&quot;</span>: <span className="s">&quot;f47ac10b-58cc-4372-a567-0e02b2c3d479&quot;</span>,
          {'\n  '}
          <span className="k">&quot;ref&quot;</span>: <span className="s">&quot;YOUR_LISTING_ID&quot;</span>
          {'\n}'}
        </DocsCodeBlock>
        <dl className="param-dl">
          <div className="param-row">
            <dt><code>galleryUrl</code></dt>
            <dd>Store this string exactly. It is the public floor-plan link.</dd>
          </div>
          <div className="param-row">
            <dt><code>ref</code></dt>
            <dd>Echo of the listing id you sent. Use it as a consistency check.</dd>
          </div>
          <div className="param-row">
            <dt><code>uniqueId</code></dt>
            <dd>
              Opaque MagicFurnish id. Do not build a <code>/floor-gallery/</code> path from it.
            </dd>
          </div>
        </dl>
        <div className="docs-note">
          If you miss the event, <code>GET /api/v2/ai/layout-by-ref?ref={'{listing_id}'}</code> on
          your API origin can return the same <code>galleryUrl</code>. Call it from your server
          with <code>X-API-Key</code> and <code>X-API-Secret</code>. The{' '}
          <Link to="/embed#parameters">embed guide</Link> lists the responses.
        </div>
      </section>

      <section className="phase docs-phase--ref" id="galleries">
        <h2 className="phase-title">Re-uploads and failures</h2>
        <ul>
          <li>
            Each successful upload creates a new gallery and a new <code>galleryUrl</code>. For
            the same listing, replace the stored URL.
          </li>
          <li>
            A shopper who edits a plan after opening it gets a separate gallery. The listing keeps
            the URL you stored.
          </li>
          <li>A failed upload emits no <code>mf:complete</code>.</li>
        </ul>
      </section>

      <div className="docs-layer-head" role="presentation">
        <span className="docs-layer-kicker">Security &amp; operations</span>
      </div>

      <section className="phase docs-phase--ref" id="security">
        <h2 className="phase-title">Security</h2>
        <ul>
          <li>
            Validate exact <code>event.origin</code> and <code>event.source</code>. The sender
            uses <code>targetOrigin: &quot;*&quot;</code>.
          </li>
          <li>The uploader does not require a shopper account or a browser API key.</li>
          <li>
            Host CSP: allow your MagicFurnish host in <code>frame-src</code>.
          </li>
          <li>
            A stored <code>galleryUrl</code> is public to anyone with the link.
          </li>
          <li>
            Keep <code>X-API-Key</code> and <code>X-API-Secret</code> on your server.
          </li>
        </ul>
      </section>

      <section className="phase docs-phase--ref" id="troubleshooting">
        <h2 className="phase-title">Troubleshooting</h2>
        <div className="step">
          <div className="troubleshooting">
            <details>
              <summary>
                <span className="accordion-title">Blank iframe</span>
                <span className="chevron" aria-hidden="true" />
              </summary>
              <div className="accordion-body">
                <p>
                  Confirm <code>iframe.src</code> is assigned and that the host Content Security
                  Policy allows your MagicFurnish host in <code>frame-src</code>.
                </p>
              </div>
            </details>
            <details>
              <summary>
                <span className="accordion-title">No <code>mf:complete</code></span>
                <span className="chevron" aria-hidden="true" />
              </summary>
              <div className="accordion-body">
                <p>
                  Confirm processing finished, the listener was registered before{' '}
                  <code>iframe.src</code>, and the listener is on the iframe&apos;s immediate
                  parent. File upload alone does not emit <code>mf:complete</code>.
                </p>
              </div>
            </details>
            <details>
              <summary>
                <span className="accordion-title">Wrong or empty link</span>
                <span className="chevron" aria-hidden="true" />
              </summary>
              <div className="accordion-body">
                <p>
                  Send both <code>host_app</code> and <code>ref</code>. Store{' '}
                  <code>galleryUrl</code> from the event. Do not build the path from{' '}
                  <code>uniqueId</code>.
                </p>
              </div>
            </details>
            <details>
              <summary>
                <span className="accordion-title">Nested iframe</span>
                <span className="chevron" aria-hidden="true" />
              </summary>
              <div className="accordion-body">
                <p>
                  <code>mf:complete</code> is delivered to the iframe&apos;s immediate parent. If
                  a widget wraps the uploader, that frame must validate the message and then
                  forward a message of its own to the page that stores the URL.
                </p>
              </div>
            </details>
          </div>
        </div>
      </section>

      <div className="docs-layer-head" role="presentation">
        <span className="docs-layer-kicker">Additional resources</span>
      </div>

      <section className="phase docs-phase--ref" id="show">
        <h2 className="phase-title">Show the plan</h2>
        <p>
          After the URL is stored, a property site or listing platform shows it with the{' '}
          <Link to="/embed">listing embed guide</Link>.
        </p>
      </section>

      <section className="phase docs-phase--ref docs-phase--follow" id="smple">
        <h2 className="phase-title">S.MPLE</h2>
        <p>
          SERHANT. and S.MPLE follow <Link to="/smple">the S.MPLE guide</Link>, including
          publishing on serhant.com. Another product uses its own <code>host_app</code> name.
        </p>
      </section>

      <div className="docs-layer-head" role="presentation">
        <span className="docs-layer-kicker">Production launch</span>
      </div>

      <section className="confirm" id="kickoff">
        <h2 className="confirm-title">Launch inputs</h2>
        <p className="confirm-lead">
          Coordinate these with MagicFurnish before go-live. They are not required to build the
          iframe itself.
        </p>
        <ol>
          <li>The MagicFurnish host that will serve the uploader and the gallery links.</li>
          <li>The <code>host_app</code> name for your product.</li>
          <li>The listing field that will store <code>galleryUrl</code>.</li>
          <li>
            An API key, if you will recover a missed link with <code>layout-by-ref</code>. Request
            one from <a href="mailto:support@magicfurnish.com">support@magicfurnish.com</a>.
          </li>
        </ol>
      </section>
    </DocsGuide>
  );
};

export default CreateGuideDocs;
